import { faLoader, faMagnifyingGlass, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Typography from '@sharedComponents/Typography';
import { getSentry } from '@utils/sentry';
import PropTypes from 'prop-types';
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';

import * as style from './style.module.css';

const CustomLoadingMessage = ({ ...props }) => (
    <components.LoadingMessage {...props}>
        <FontAwesomeIcon icon={faLoader} spin className={style.icon} />
    </components.LoadingMessage>
);

const CustomControl = ({ children, ...props }) => (
    <components.Control {...props} className={props.menuIsOpen && props.selectProps.inputValue.length ? style.menuIsOpen : ''}>
        <FontAwesomeIcon icon={faMagnifyingGlass} className={`${style.icon} ${style.magnifier}`} />
        {children}
        {(!!props.selectProps.inputValue.length || (props.hasValue && props.selectProps.controlShouldRenderValue)) && (
            <FontAwesomeIcon
                icon={faXmark}
                className={`${style.icon} ${style.xMark}`}
                onClick={() => {
                    props.selectProps.onClear();
                    props.clearValue();
                }}
            />
        )}
    </components.Control>
);

const CustomMenu = ({ children, ...props }) => (
    <components.Menu {...props} className={props.selectProps.inputValue.length ? '' : style.inputEmpty}>
        {children}
        {!!props.options.length && props.selectProps.MenuAction && <props.selectProps.MenuAction {...props} />}
    </components.Menu>
);

const CustomOption = ({ children, ...props }) => {
    return (
        <components.Option {...props} className={props.isFocused ? style.optionIsFocus : ''}>
            {props.selectProps.Option ? <props.selectProps.Option {...props}>{children}</props.selectProps.Option> : children}
        </components.Option>
    );
};

const SearchBar = ({
    search,
    minChars = 3,
    noOptionsMessage = "We haven't found any suggestions matching your search.\nPlease check or modify your search.",
    MenuAction,
    Option,
    placeholder,
    className,
    onSelect,
    controlShouldRenderValue = true,
    onClear = () => {},
    disabled = false,
    hideMenu = false,
    onTextChange,
    debounceMs = 0,
    compact = false,
    ref,
    customContainerStyles,
    autoFocus = true,
    ariaLabel,
    embedded = false,
}) => {
    const { t } = useTranslation();
    const timeoutRef = useRef();
    const selectRef = useRef();
    const textDebounceRef = useRef();
    const [inputValue, setInputValue] = useState('');
    const [defaultOptions, setDefaultOptions] = useState([]);

    useEffect(() => () => clearTimeout(textDebounceRef.current), []);

    const emitTextChange = (value) => {
        if (debounceMs > 0) {
            if (textDebounceRef.current) clearTimeout(textDebounceRef.current);
            textDebounceRef.current = setTimeout(() => onTextChange(value), debounceMs);
        } else {
            onTextChange(value);
        }
    };

    useImperativeHandle(ref, () => ({
        manualSearch: ({ inputValue }) => {
            search(inputValue).then((data) => {
                setDefaultOptions(data);
                setInputValue(inputValue);
            });

            selectRef?.current?.focus();
        },
        focus: () => selectRef?.current?.focus(),
        unFocus: () => selectRef?.current?.blur(),
        setInputValue: (value) => {
            // A pending debounced onTextChange would override the value the caller just forced.
            clearTimeout(textDebounceRef.current);
            setInputValue(value);
        },
        setDefaultOptions: (options) => setDefaultOptions(options),
    }));

    const promiseOptions = (inputValue) =>
        new Promise((resolve) => {
            if (inputValue.length < minChars) return resolve([]);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(
                () =>
                    search(inputValue)
                        .then((data) => resolve(data))
                        .catch((error) => {
                            getSentry()
                                .then((sentry) => sentry?.captureException(error))
                                .catch(() => {});
                            resolve([]);
                        }),
                200,
            );
        });

    const customComponents = useMemo(
        () => ({
            LoadingIndicator: null,
            DropdownIndicator: null,
            IndicatorSeparator: null,
            IndicatorsContainer: () => null,
            LoadingMessage: CustomLoadingMessage,
            Control: CustomControl,
            Menu: hideMenu ? () => null : CustomMenu,
            Option: CustomOption,
            NoOptionsMessage: ({ ...props }) => {
                if (!props.selectProps.inputValue.length) return null;
                return (
                    <components.NoOptionsMessage {...props}>
                        {props.selectProps.inputValue.length < minChars ? (
                            <Typography className={style.noOptionsMessage} center>
                                {t('Enter at least {{count}} characters for suggestions.', { count: minChars })}
                            </Typography>
                        ) : (
                            <Typography className={style.noOptionsMessage} center>
                                {t(noOptionsMessage)}
                            </Typography>
                        )}
                    </components.NoOptionsMessage>
                );
            },
        }),
        [t, minChars, noOptionsMessage, hideMenu],
    );

    return (
        <AsyncSelect
            ref={selectRef}
            unstyled
            inputValue={inputValue}
            defaultOptions={defaultOptions}
            onInputChange={(value, actionMeta) => {
                if (onTextChange) {
                    if (actionMeta.action === 'input-change') {
                        setInputValue(value);
                        emitTextChange(value);
                    }
                    return;
                }
                setInputValue(value);
            }}
            /* eslint-disable-next-line jsx-a11y/no-autofocus */
            autoFocus={autoFocus}
            aria-label={ariaLabel}
            isDisabled={disabled}
            openMenuOnFocus={!hideMenu}
            menuIsOpen={hideMenu ? false : undefined}
            onChange={onSelect}
            loadOptions={promiseOptions}
            classNamePrefix="react-select"
            className={className}
            controlShouldRenderValue={controlShouldRenderValue}
            isClearable={controlShouldRenderValue}
            backspaceRemovesValue={controlShouldRenderValue}
            styles={{
                container: (base) => ({ ...base, ...customContainerStyles }),
            }}
            classNames={{
                container: () => style.container,
                input: () => style.input,
                placeholder: () => style.placeholder,
                menu: () => style.menu,
                control: () =>
                    `${style.control} ${compact ? style.compact : ''} ${disabled ? style.controlDisabled : ''} ${embedded ? style.embedded : ''}`,
                loadingMessage: () => style.loadingMessage,
                menuList: () => style.menuList,
                groupHeading: () => style.groupHeading,
                option: () => style.option,
                singleValue: () => style.singleValue,
            }}
            components={customComponents}
            placeholder={placeholder ?? null}
            MenuAction={MenuAction}
            Option={Option}
            minChars={minChars}
            onClear={() => {
                clearTimeout(textDebounceRef.current);
                setInputValue('');
                onClear();
            }}
        />
    );
};

SearchBar.propTypes = {
    search: PropTypes.func,
    minChars: PropTypes.number,
    noOptionsMessage: PropTypes.string,
    menuAction: PropTypes.object,
    Option: PropTypes.node,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    onSelect: PropTypes.func,
    controlShouldRenderValue: PropTypes.bool,
    onClear: PropTypes.func,
    disabled: PropTypes.bool,
    hideMenu: PropTypes.bool,
    onTextChange: PropTypes.func,
    debounceMs: PropTypes.number,
    compact: PropTypes.bool,
    autoFocus: PropTypes.bool,
    ariaLabel: PropTypes.string,
    embedded: PropTypes.bool,
};

export default SearchBar;
